/**
 * Page admin pour gérer les notifications client ETA :
 * - Envoyer un récap d'intervention manuellement
 * - Envoyer une prévention à une commune/collectivité
 * - Envoyer un document traçable (devis, contrat, avenant...)
 * - Envoyer par email un accès portail client
 * - Voir l'historique des envois
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Send,
  FileCheck,
  Bell,
  QrCode,
  Mail,
  History,
  Loader2,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationsETAPage() {
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [societes, setSocietes] = useState([]);
  const [societeId, setSocieteId] = useState('');
  const [tab, setTab] = useState('document');
  const [logs, setLogs] = useState([]);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tenant/societes`, auth).then((r) => {
      const list = r.data.societes || r.data || [];
      // Filtrer les ETA
      const etas = list.filter(
        (s) =>
          (s.type || '').toLowerCase().includes('eta') ||
          (s.nom || '').toUpperCase().includes('ETA ') ||
          (s.nom || '').toUpperCase().includes('SAUVAYRE')
      );
      setSocietes(etas);
      if (etas.length === 1) setSocieteId(etas[0].id);
    });
    loadLogs();
  }, []); // eslint-disable-line

  const loadLogs = async () => {
    try {
      const r = await axios.get(`${API}/eta-notifications/logs?limit=50`, auth);
      setLogs(r.data.logs || []);
    } catch {}
  };

  const generateQR = async () => {
    if (!societeId) {
      toast.error('Sélectionnez une ETA');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/eta-notifications/qr-portail/${societeId}`,
        auth
      );
      setQrData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur QR');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="notifications-eta-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Notifications Client ETA</h1>
        <p className="text-slate-500 text-sm mt-1">
          Emails auto post-intervention, préventions communes, documents
          traçables, accès portail.
        </p>
      </div>

      {/* Selecteur ETA */}
      <div className="mb-6 p-4 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <label className="block text-xs font-medium text-slate-500 mb-2">
          ETA émettrice
        </label>
        <select
          data-testid="select-societe-eta"
          value={societeId}
          onChange={(e) => setSocieteId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
        >
          <option value="">-- Choisir --</option>
          {societes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nom}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { id: 'document', label: 'Document traçable', icon: FileCheck },
          { id: 'prevention', label: 'Prévention commune', icon: Bell },
          { id: 'acces', label: 'Accès portail', icon: Mail },
          { id: 'qr', label: 'QR code', icon: QrCode },
          { id: 'logs', label: 'Historique', icon: History },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                tab === t.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'document' && (
        <DocumentForm societeId={societeId} onSent={loadLogs} auth={auth} />
      )}
      {tab === 'prevention' && (
        <PreventionForm societeId={societeId} onSent={loadLogs} auth={auth} />
      )}
      {tab === 'acces' && (
        <AccesForm societeId={societeId} onSent={loadLogs} auth={auth} />
      )}
      {tab === 'qr' && (
        <QRSection
          societeId={societeId}
          qrData={qrData}
          onGenerate={generateQR}
          loading={loading}
        />
      )}
      {tab === 'logs' && <LogsTable logs={logs} onRefresh={loadLogs} />}
    </div>
  );
}

function DocumentForm({ societeId, onSent, auth }) {
  const [form, setForm] = useState({
    type_document: 'devis',
    client_email: '',
    client_nom: '',
    titre: '',
    resume: '',
    montant_ttc: '',
    document_id: `DOC-${Date.now()}`,
  });
  const [loading, setLoading] = useState(false);
  const [lastUrl, setLastUrl] = useState('');

  const submit = async () => {
    if (!societeId) {
      toast.error('Sélectionnez une ETA');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        societe_id: societeId,
        montant_ttc: form.montant_ttc ? parseFloat(form.montant_ttc) : null,
      };
      const r = await axios.post(
        `${API}/eta-notifications/envoyer-document`,
        payload,
        auth
      );
      if (r.data.sent) {
        toast.success('Email envoyé + lien traçable généré');
      } else {
        toast.warning(`Lien généré. Email: ${r.data.error || 'non envoyé'}`);
      }
      setLastUrl(r.data.url);
      navigator.clipboard.writeText(r.data.url);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-4">
      <h3 className="font-semibold">Envoyer un devis / contrat / avenant</h3>
      <div className="grid md:grid-cols-2 gap-3">
        <select
          data-testid="doc-type"
          value={form.type_document}
          onChange={(e) => setForm({ ...form, type_document: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        >
          <option value="devis">Devis</option>
          <option value="contrat">Contrat</option>
          <option value="avenant">Avenant</option>
          <option value="bon_livraison">Bon de livraison</option>
          <option value="facture">Facture</option>
        </select>
        <input
          data-testid="doc-titre"
          placeholder="Titre / Référence"
          value={form.titre}
          onChange={(e) => setForm({ ...form, titre: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="doc-client-nom"
          placeholder="Nom du client"
          value={form.client_nom}
          onChange={(e) => setForm({ ...form, client_nom: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="doc-client-email"
          type="email"
          placeholder="Email client"
          value={form.client_email}
          onChange={(e) => setForm({ ...form, client_email: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="doc-montant"
          type="number"
          placeholder="Montant TTC (€)"
          value={form.montant_ttc}
          onChange={(e) => setForm({ ...form, montant_ttc: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
      </div>
      <textarea
        data-testid="doc-resume"
        placeholder="Résumé / termes précis du document (ce que le client accepte en signant)"
        value={form.resume}
        onChange={(e) => setForm({ ...form, resume: e.target.value })}
        rows={4}
        className="w-full px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
      />
      <button
        data-testid="doc-send-btn"
        onClick={submit}
        disabled={loading}
        className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold flex items-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Envoyer au client
      </button>
      {lastUrl && (
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
          ✅ Lien traçable (copié) :{' '}
          <a href={lastUrl} target="_blank" rel="noreferrer" className="text-emerald-700 underline">
            {lastUrl}
          </a>
        </div>
      )}
    </div>
  );
}

function PreventionForm({ societeId, onSent, auth }) {
  const [form, setForm] = useState({
    destinataires: '',
    date_prevue: '',
    lieu: '',
    type_travaux: '',
    chauffeur: '',
    client_nom: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!societeId) return toast.error('Sélectionnez une ETA');
    setLoading(true);
    try {
      const payload = {
        societe_id: societeId,
        destinataires_email: form.destinataires
          .split(/[,;\s]+/)
          .map((e) => e.trim())
          .filter(Boolean),
        date_prevue: form.date_prevue,
        lieu: form.lieu,
        type_travaux: form.type_travaux,
        chauffeur: form.chauffeur,
        notes: form.notes,
        client_nom: form.client_nom,
      };
      const r = await axios.post(
        `${API}/eta-notifications/envoyer-prevention`,
        payload,
        auth
      );
      toast.success(`${r.data.sent}/${r.data.total} emails envoyés`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-4">
      <h3 className="font-semibold">Prévenir une commune / collectivité</h3>
      <div className="grid md:grid-cols-2 gap-3">
        <input
          data-testid="prev-destinataires"
          placeholder="Emails (séparés par virgule ou espace)"
          value={form.destinataires}
          onChange={(e) => setForm({ ...form, destinataires: e.target.value })}
          className="md:col-span-2 px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="prev-date"
          type="date"
          value={form.date_prevue}
          onChange={(e) => setForm({ ...form, date_prevue: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="prev-lieu"
          placeholder="Lieu (chemin, parcelle...)"
          value={form.lieu}
          onChange={(e) => setForm({ ...form, lieu: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="prev-type"
          placeholder="Type de travaux (débroussaillage, phyto...)"
          value={form.type_travaux}
          onChange={(e) => setForm({ ...form, type_travaux: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="prev-chauffeur"
          placeholder="Chauffeur"
          value={form.chauffeur}
          onChange={(e) => setForm({ ...form, chauffeur: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="prev-client"
          placeholder="Client (optionnel)"
          value={form.client_nom}
          onChange={(e) => setForm({ ...form, client_nom: e.target.value })}
          className="md:col-span-2 px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
      </div>
      <textarea
        data-testid="prev-notes"
        placeholder="Notes (signalisation, horaires, contraintes...)"
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        rows={3}
        className="w-full px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
      />
      <button
        data-testid="prev-send-btn"
        onClick={submit}
        disabled={loading}
        className="px-5 py-2.5 rounded-lg bg-amber-600 text-white font-semibold flex items-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
        Envoyer la prévention
      </button>
    </div>
  );
}

function AccesForm({ societeId, onSent, auth }) {
  const [form, setForm] = useState({
    client_email: '',
    client_nom: '',
    mot_de_passe: '',
  });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!societeId) return toast.error('Sélectionnez une ETA');
    if (!form.mot_de_passe) {
      const pw = Math.random().toString(36).slice(-10);
      setForm({ ...form, mot_de_passe: pw });
      toast.info(`MDP auto : ${pw}`);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/eta-notifications/envoyer-acces-portail`,
        { societe_id: societeId, ...form },
        auth
      );
      toast.success(
        r.data.sent ? 'Accès envoyé par email (avec QR code)' : 'Envoi échoué'
      );
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-4">
      <h3 className="font-semibold">Envoyer les identifiants portail par email</h3>
      <div className="grid md:grid-cols-2 gap-3">
        <input
          data-testid="acces-email"
          placeholder="Email client"
          value={form.client_email}
          onChange={(e) => setForm({ ...form, client_email: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="acces-nom"
          placeholder="Nom client"
          value={form.client_nom}
          onChange={(e) => setForm({ ...form, client_nom: e.target.value })}
          className="px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
        <input
          data-testid="acces-password"
          placeholder="Mot de passe portail (à distribuer)"
          value={form.mot_de_passe}
          onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })}
          className="md:col-span-2 px-3 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900"
        />
      </div>
      <p className="text-xs text-slate-500">
        💡 Assurez-vous d'avoir créé le compte via <em>/admin/portail-eta</em>{' '}
        avant d'envoyer le mot de passe.
      </p>
      <button
        data-testid="acces-send-btn"
        onClick={submit}
        disabled={loading}
        className="px-5 py-2.5 rounded-lg bg-cyan-600 text-white font-semibold flex items-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
        Envoyer
      </button>
    </div>
  );
}

function QRSection({ societeId, qrData, onGenerate, loading }) {
  return (
    <div className="p-6 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <h3 className="font-semibold mb-4">QR code vers l'espace client ETA</h3>
      <p className="text-sm text-slate-500 mb-4">
        Imprime-le sur tes bons de chantier : les clients scannent et arrivent
        directement sur le login du portail.
      </p>
      <button
        data-testid="qr-generate-btn"
        onClick={onGenerate}
        disabled={loading || !societeId}
        className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold flex items-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
        Générer
      </button>
      {qrData && (
        <div className="mt-6 text-center">
          <img
            src={qrData.qr_data_uri}
            alt="QR portail"
            className="mx-auto w-64 h-64 border rounded-lg"
            data-testid="qr-image"
          />
          <div className="mt-3 text-xs text-slate-500">
            URL : <a href={qrData.url} className="text-emerald-600 underline">{qrData.url}</a>
          </div>
          <a
            href={qrData.qr_data_uri}
            download="qr-portail-eta.png"
            className="inline-block mt-3 text-sm text-emerald-600 underline"
          >
            Télécharger le PNG
          </a>
        </div>
      )}
    </div>
  );
}

function LogsTable({ logs, onRefresh }) {
  return (
    <div className="p-6 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Historique des envois</h3>
        <button
          onClick={onRefresh}
          className="text-sm text-emerald-600 hover:underline"
        >
          Actualiser
        </button>
      </div>
      <div className="space-y-2">
        {logs.map((l) => (
          <div
            key={l.id || l.sent_at}
            className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900 flex items-start justify-between gap-3 text-sm"
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-xs bg-slate-200 dark:bg-slate-700">
                  {l.type}
                </span>
                <span className="font-medium">{l.to}</span>
              </div>
              <div className="text-slate-500 text-xs mt-1">{l.subject}</div>
            </div>
            <div className="text-right shrink-0">
              {l.success ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500 inline" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500 inline" />
              )}
              <div className="text-xs text-slate-400 mt-1">
                {new Date(l.sent_at).toLocaleString('fr-FR')}
              </div>
            </div>
          </div>
        ))}
        {logs.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-8">
            Aucun envoi pour l'instant.
          </div>
        )}
      </div>
    </div>
  );
}
